import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  forwardRef,
  Injector,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormArray,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ITranslateModel,
  IMultilingualConfig,
  ILanguageOption,
  CATEGORY_LANGUAGES,
} from '../../../interfaces/ITranslateModel';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-multilingual-input',
  templateUrl: './multilingual-input.component.html',
  styleUrls: ['./multilingual-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultilingualInputComponent),
      multi: true,
    },
  ],
})
export class MultilingualInputComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() config: IMultilingualConfig = {
    fieldNames: ['Name'],
    supportedLanguages: CATEGORY_LANGUAGES,
    defaultLanguage: 'en',
  };
  @Input() label: string = 'Multilingual Input';
  @Input() placeholder: string = 'Enter value';
  @Input() required: boolean = false;
  @Input() maxLength: number = 255;
  @Input() disabled: boolean = false;
  @Input() dynamicFields: string[] = [];

  @Output() translateModelListChange = new EventEmitter<ITranslateModel[]>();

  multilingualForm: FormGroup;
  availableLanguages: ILanguageOption[] = [];
  private destroy$ = new Subject<void>();

  // ControlValueAccessor
  private onChange = (value: ITranslateModel[]) => {};
  private onTouched = () => {};

  private _isUpdatingForm = false;
  private _lastEmittedValue: string = '';

  constructor(private fb: FormBuilder, private injector: Injector) {
    this.initForm();
  }

  ngOnInit(): void {
    this.availableLanguages = [...this.config.supportedLanguages];

    // Use dynamic fields if provided, otherwise use config fieldNames
    if (this.dynamicFields && this.dynamicFields.length > 0) {
      this.config = {
        ...this.config,
        fieldNames: this.dynamicFields,
      };
    }

    this.addDefaultLanguage();
    this.setupFormSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: ITranslateModel[]): void {
    if (value && Array.isArray(value)) {
      const currentStringified = JSON.stringify(
        this.generateTranslateModelList()
      );
      const newStringified = JSON.stringify(value);

      // Only update if the value actually changed to prevent unnecessary re-renders
      if (currentStringified !== newStringified) {
        this._isUpdatingForm = true;
        this.populateForm(value);
        // Use longer timeout to ensure form is stable before allowing new updates
        setTimeout(() => {
          this._isUpdatingForm = false;
        }, 150);
      }
    }
  }

  registerOnChange(fn: (value: ITranslateModel[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.multilingualForm.disable();
    } else {
      this.multilingualForm.enable();
    }
  }

  private initForm(): void {
    this.multilingualForm = this.fb.group({
      languages: this.fb.array([]),
    });
  }

  get languagesArray(): FormArray {
    return this.multilingualForm.get('languages') as FormArray;
  }

  private addDefaultLanguage(): void {
    const defaultLang = this.config.supportedLanguages.find(
      (lang) => lang.code === this.config.defaultLanguage
    );
    if (defaultLang) {
      this.addLanguage(defaultLang);
    }
  }

  addLanguage(language: ILanguageOption): void {
    // Check if language already exists
    const exists = this.languagesArray.controls.some(
      (control) => control.get('languageCode')?.value === language.code
    );

    if (exists) return;

    const languageGroup = this.createLanguageGroup(language);
    this.languagesArray.push(languageGroup);

    // Remove from available languages
    this.availableLanguages = this.availableLanguages.filter(
      (lang) => lang.code !== language.code
    );
  }

  removeLanguage(index: number): void {
    const languageCode = this.languagesArray
      .at(index)
      .get('languageCode')?.value;

    // Don't allow removing the default language if it's the only one
    if (
      languageCode === this.config.defaultLanguage &&
      this.languagesArray.length === 1
    ) {
      return;
    }

    this.languagesArray.removeAt(index);

    // Add back to available languages
    const language = this.config.supportedLanguages.find(
      (lang) => lang.code === languageCode
    );
    if (language) {
      this.availableLanguages.push(language);
      this.availableLanguages.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  private createLanguageGroup(language: ILanguageOption): FormGroup {
    const group: any = {
      languageCode: [language.code, Validators.required],
      languageName: [language.name],
    };

    // Add form controls for each field name
    this.config.fieldNames.forEach((fieldName) => {
      group[fieldName.toLowerCase()] = [
        '',
        this.required
          ? [Validators.required, Validators.maxLength(this.maxLength)]
          : [Validators.maxLength(this.maxLength)],
      ];
    });

    return this.fb.group(group);
  }

  private setupFormSubscription(): void {
    this.multilingualForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300), // Reduced from 500ms for better responsiveness
        distinctUntilChanged((prev, curr) => {
          // Better comparison for complex objects
          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe(() => {
        if (!this._isUpdatingForm) {
          const translateModelList = this.generateTranslateModelList();
          const stringifiedList = JSON.stringify(translateModelList);

          // Only emit if the data actually changed
          if (this._lastEmittedValue !== stringifiedList) {
            this.translateModelListChange.emit(translateModelList);
            this.onChange(translateModelList);
            this._lastEmittedValue = stringifiedList;
          }
        }
      });
  }

  private generateTranslateModelList(): ITranslateModel[] {
    const result: ITranslateModel[] = [];

    this.languagesArray.controls.forEach((languageControl) => {
      const languageCode = languageControl.get('languageCode')?.value;

      this.config.fieldNames.forEach((fieldName) => {
        const translatedValue =
          languageControl.get(fieldName.toLowerCase())?.value || '';

        result.push({
          languageCode,
          fieldName,
          translatedValue,
        });
      });
    });

    return result;
  }

  private populateForm(translateModelList: ITranslateModel[]): void {
    // Clear existing form array
    while (this.languagesArray.length > 0) {
      this.languagesArray.removeAt(0);
    }

    // Reset available languages
    this.availableLanguages = [...this.config.supportedLanguages];

    // Group by language code
    const languageGroups: { [key: string]: ITranslateModel[] } = {};
    translateModelList.forEach((item) => {
      if (!languageGroups[item.languageCode]) {
        languageGroups[item.languageCode] = [];
      }
      languageGroups[item.languageCode].push(item);
    });

    // Create form groups for each language
    Object.keys(languageGroups).forEach((languageCode) => {
      const language = this.config.supportedLanguages.find(
        (lang) => lang.code === languageCode
      );

      if (language) {
        const languageGroup = this.createLanguageGroup(language);

        // Populate field values
        languageGroups[languageCode].forEach((item) => {
          const controlName = item.fieldName.toLowerCase();
          const control = languageGroup.get(controlName);
          if (control) {
            control.setValue(item.translatedValue);
          }
        });

        this.languagesArray.push(languageGroup);

        // Remove from available languages
        this.availableLanguages = this.availableLanguages.filter(
          (lang) => lang.code !== languageCode
        );
      }
    });

    // If no languages were added, add default
    if (this.languagesArray.length === 0) {
      this.addDefaultLanguage();
    }
  }

  getFieldLabel(fieldName: string): string {
    const translate = this.injector.get(TranslateService);
    return translate.instant(fieldName);
  }

  getPlaceholder(fieldName: string): string {
    const translate = this.injector.get(TranslateService);
    return translate.instant(`Enter ${fieldName}`);
  }

  isFieldRequired(fieldName: string): boolean {
    return this.required;
  }

  hasError(languageIndex: number, fieldName: string): boolean {
    const control = this.languagesArray
      .at(languageIndex)
      .get(fieldName.toLowerCase());
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getErrorMessage(languageIndex: number, fieldName: string): string {
    const control = this.languagesArray
      .at(languageIndex)
      .get(fieldName.toLowerCase());
    const translate = this.injector.get(TranslateService);

    if (control?.errors) {
      if (control.errors['required']) {
        return translate.instant(`${fieldName} is required`);
      }
      if (control.errors['maxlength']) {
        return translate.instant(
          `${fieldName} cannot exceed ${this.maxLength} characters`
        );
      }
    }
    return '';
  }

  onTouchedField(): void {
    this.onTouched();
  }

  // Helper methods for template
  getLanguageFlag(languageCode: string): string {
    const language = this.config.supportedLanguages.find(
      (l) => l.code === languageCode
    );
    return language?.flag || '';
  }

  getLanguageName(languageCode: string): string {
    const language = this.config.supportedLanguages.find(
      (l) => l.code === languageCode
    );
    return language?.name || '';
  }

  getFieldValue(languageGroup: any, fieldName: string): string {
    return languageGroup.get(fieldName.toLowerCase())?.value || '';
  }

  getFieldValueLength(languageGroup: any, fieldName: string): number {
    return this.getFieldValue(languageGroup, fieldName).length;
  }

  canRemoveLanguage(languageCode: string): boolean {
    return !(
      languageCode === this.config.defaultLanguage &&
      this.languagesArray.length === 1
    );
  }

  // Track function for ngFor to improve performance
  trackByLanguageCode(index: number, languageGroup: any): string {
    return languageGroup.get('languageCode')?.value || index;
  }

  // Track function for field names
  trackByFieldName(index: number, fieldName: string): string {
    return fieldName;
  }
}
